import { TemplateFormType } from 'components/ui/composed/template/TemplateContext';
import { ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';

/**
 * Which template levels each ICS2 ENS message type accepts.
 *
 * Mirrors the backend's `Ics2EnsMessageTopology` (`hasHouseConsignments` / `hasMasterGoodsItems` /
 * `hasHouseGoodsItems`). Sending a level the message type does not support is rejected outright,
 * e.g. F50 has no master goods item class, so posting one fails with
 * "F50 does not support master goods items".
 *
 * The keys are the frontend's own level names. `product` is the master goods item — the ICS2
 * template API spells it `masterGoodsItem`, which the boundary mapper applies.
 */
export const ICS2_ENS_TEMPLATE_LEVELS: Record<ICS2MessageTypes, TemplateFormType[]> = {
    [ICS2MessageTypes.F11]: ['master', 'houseConsignment', 'product', 'houseGoodsItem'],
    [ICS2MessageTypes.F50]: ['master', 'houseConsignment', 'houseGoodsItem'],
};

const LEVEL_LABELS: Record<TemplateFormType, string> = {
    master: 'Master Information',
    houseConsignment: 'House Consignment',
    product: 'Master Item',
    houseGoodsItem: 'House Item',
};

export const isIcs2EnsMessageType = (formType: string | undefined): formType is ICS2MessageTypes =>
    !!formType && formType in ICS2_ENS_TEMPLATE_LEVELS;

export const getIcs2EnsTemplateLevels = (formType: string | undefined): TemplateFormType[] =>
    isIcs2EnsMessageType(formType) ? ICS2_ENS_TEMPLATE_LEVELS[formType] : [];

/** Segmented-control options for the template builder, ordered as the levels are declared. */
export const getIcs2EnsTemplateFormOptions = (
    formType: string | undefined
): { label: string; value: TemplateFormType }[] =>
    getIcs2EnsTemplateLevels(formType).map((value) => ({ label: LEVEL_LABELS[value], value }));
